import { PhoneNumberRecord } from "common/records/PhoneNumber";
import { format as dateFormat } from "date-fns";
import { List } from "immutable";

type DateFormatType =
  | "shortDate"
  | "shortName"
  | "shortDate-hyphenated"
  | "shortTime"
  | "shortNameTime"
  | "shortTimeMilitary"
  | "short-hyphenated"
  | "shortHyphenatedUnderscore"
  | "short"
  | "shortDateWithDay"
  | "monthYearShort";

export function DateFormat(date: any, format: DateFormatType = "short", invalidDateText = "Invalid date"): string {
  if (!date || isNaN(new Date(date).getTime())) {
    return invalidDateText;
  }
  let formatString: string;

  switch (format) {
    case "shortDate":
      formatString = "MM/dd/yy";
      break;
    case "shortName":
      formatString = "MMM d, yyyy";
      break;
    case "shortDate-hyphenated":
      formatString = "yyyy-MM-dd";
      break;
    case "shortTime":
      formatString = "h:mm a";
      break;
    case "shortNameTime":
      formatString = "MMM d, yyyy h:mm a";
      break;
    case "shortTimeMilitary":
      formatString = "HH:mm";
      break;
    case "short-hyphenated":
      formatString = "MM-dd-yy h:mm a";
      break;
    case "shortHyphenatedUnderscore":
      formatString = "MM-dd-yy_h-mm";
      break;
    case "shortDateWithDay":
      formatString = "EEE, do MMM";
      break;
    case "monthYearShort":
      formatString = "MMM yyyy";
      break;
    case "short":
    default:
      formatString = "MM/dd/yy h:mm a";
      break;
  }

  const formattedDate = dateFormat(new Date(date), formatString);
  return formattedDate;
}

export function PhoneNumberFormat(number: string): string {
  let newVal = "";
  let extra = "";
  if (number.length <= 12) {
    newVal = number.replace(/\D/g, "");
  } else {
    newVal = number.slice(0, 12).replace(/\D/g, "");
    extra = number.slice(12);
  }

  if (newVal.length === 0) {
    newVal = "";
  } else if (newVal.length <= 3) {
    newVal = newVal.replace(/^(\d{0,3})/, "$1");
  } else if (newVal.length <= 6) {
    newVal = newVal.replace(/^(\d{0,3})(\d{0,3})/, "$1-$2");
  } else {
    newVal = newVal.replace(/^(\d{0,3})(\d{0,3})(.*)/, "$1-$2-$3");
  }

  return `${newVal}${extra}`.trim();
}

export function CurrencyFormat(amount: number, fractionDigits = 2): string {
  const rounded: string = amount.toFixed(fractionDigits).replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");

  return `\$${rounded}`;
}

export const uuidRegex = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/;

export const SnakeToCamel = (value: string, firstLetterUppercase = false): string => {
  if (value.match(uuidRegex)) {
    return value;
  }

  let transformed = value.replace(/([-_][a-z|0-9])/gi, ($1) => {
    return $1.toUpperCase().replace("-", "").replace("_", "");
  });

  transformed =
    (firstLetterUppercase ? transformed.charAt(0).toUpperCase() : transformed.charAt(0).toLowerCase()) +
    transformed.slice(1);

  if (value.startsWith("_") && !transformed.startsWith("_")) {
    transformed = `_${transformed}`;
  }

  return transformed;
};

export const CamelToSnake = (value: string, underScoreNumbers = true): string => {
  if (value.match(uuidRegex)) {
    return value;
  }

  let transformed = value;
  if (underScoreNumbers) {
    transformed = transformed.replace(/([A-Z|0-9])/g, "_$1");
  } else {
    transformed = transformed.replace(/([A-Z])/g, "_$1");
  }

  transformed = transformed.replace(/^_/, "").toLowerCase();

  if (value.startsWith("_") && !transformed.startsWith("_")) {
    transformed = `_${transformed}`;
  }

  return transformed;
};

export const SnakeToCamelKeys = (data: any): any => {
  if (data === Object(data) && !Array.isArray(data) && typeof data !== "function") {
    const newData = {};
    Object.keys(data).forEach((k) => {
      //@ts-ignore
      newData[SnakeToCamel(k)] = SnakeToCamelKeys(data[k]);
    });
    return newData;
  } else if (Array.isArray(data)) {
    return data.map((i) => {
      return SnakeToCamelKeys(i);
    });
  }
  return data;
};

export const CamelToSnakeKeys = (data: any): any => {
  if (data === Object(data) && !Array.isArray(data) && typeof data !== "function") {
    const newData = {};
    Object.keys(data).forEach((k) => {
      //@ts-ignore
      newData[CamelToSnake(k)] = CamelToSnakeKeys(data[k]);
    });
    return newData;
  } else if (Array.isArray(data)) {
    return data.map((i) => {
      return CamelToSnakeKeys(i);
    });
  }
  return data;
};

export const StylizedQuotesToSimpleQuotes = (value: string): string => {
  return value.replace(/[\u201C\u201D]/g, '\\"').replace(/[\u2018\u2019]/g, "'");
};

/**
 * Takes an List of phone numbers and returns mobile number first, then next phone number if a mobile does not exist, and formats into string
 * @phoneNumbers List of phone numbers with type
 * @returns string
 */
export const SelectMobilePhoneNumber = (phoneNumbers: List<PhoneNumberRecord>): string => {
  let phoneNumber = <string>phoneNumbers
    .filter((pn) => /sms|mobile|cell/.test(pn.name?.toLowerCase() || ""))
    .map((pn) => pn.number?.replace(/\D/g, ""))
    .first();

  if (!phoneNumber) {
    phoneNumber = <string>phoneNumbers.map((pn) => pn.number?.replace(/\D/g, "")).first();
  }

  if (!phoneNumber) {
    return "";
  }

  return phoneNumber;
};

export function PercentageFormat(amount: number): string {
  const rounded: string = parseFloat(amount.toFixed(2))
    .toString()
    .replace(/\d(?=(\d{3})+\.)/g, "$&,");

  return `${rounded}%`;
}

// Common Titles Helper function
export function CommonTitles(name: ValidCommonTitles): string {
  return commonTitles[name];
}

export type ValidCommonTitles =
  | "estimate"
  | "contract"
  | "opening_name"
  | "estimator"
  | "inspection"
  | "finance"
  | "finance_options"
  | "price_presentation"
  | "subtotal"
  | "total"
  | "description";

// Common Titles - What OCC calls things (development/support/leadership)
// These can be changed by the customer, but should not be changed in the admin screen
const commonTitles = {
  estimate: "Proposal",
  contract: "Agreement",
  opening_name: "Name",
  estimator: "Estimate",
  inspection: "Inspection",
  finance: "Investment",
  finance_options: "Finance Options",
  price_presentation: "Price Presentation",
  subtotal: "Subtotal",
  total: "Total",
  description: "Description",
};
